import { useState, useEffect, useRef } from 'react';
import H3 from '@sdk/components/ui/typography/H3';
import gettext from '@sdk/lang/gettext';
import Text16 from '@sdk/components/ui/typography/Text16';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import TextField from '@sdk/components/forms/TextField';
import PickerField from '@sdk/components/forms/PickerField';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { INPUT_HEIGHT } from '@sdk/components/forms/constants';
import FieldError from '@sdk/components/forms/components/FieldError';
import { api } from '@sdk/api/useSlingApi';
import { Organisation } from '@sdk/authentication/types/OrganisationType';
import Column from '@sdk/components/ui/spacing/Column';
import { GENERIC_SERVER_ERROR } from '@sdk/login/constants';
import useAuth from '@sdk/authentication/useAuth';
import Button from '@sdk/components/ui/buttons/Button';

import {
  EMPLOYEES_COUNT_OPTIONS,
  BUSINESS_CATEGORIES_OPTIONS,
  OTHER_BUSINESS_CATEGORY,
} from '@web/onboarding/constants';

import { ButtonContainer, Container } from './createOrgPage.styles';

const MAX_INPUT_LENGTH = 50;

type CreateOrgPayload = {
  name: string;
  businessCategory: string;
  customIndustry?: string;
};

const createOrg = async (
  payload: CreateOrgPayload
): Promise<{ response: Response; data: Organisation }> => {
  return api.httpPost('organisations', payload);
};

type Props = {
  switchOrg: (org: { id: number }) => Promise<void>;
  returnToDashboard?: () => void;
};

export const CreateOrgPage = ({ switchOrg, returnToDashboard }: Props) => {
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [customIndustry, setCustomIndustry] = useState('');
  const [employeesCount, setEmployeesCount] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const focusFieldRef = useRef(null);

  useEffect(() => {
    focusFieldRef.current?.focus();
  }, []);

  const validate = (): boolean => {
    let valid = true;
    if (
      !companyName.trim() ||
      companyName?.length > MAX_INPUT_LENGTH ||
      !industry ||
      (industry === OTHER_BUSINESS_CATEGORY &&
        (!customIndustry.trim() || customIndustry?.length > MAX_INPUT_LENGTH))
    ) {
      valid = false;
    }

    return valid;
  };

  const submit = async (): Promise<void> => {
    setIsSubmitted(true);
    setServerErrors([]);

    if (!validate()) {
      throw gettext('Form invalid');
    }

    const orgData: CreateOrgPayload = {
      name: companyName.trim(),
      businessCategory: industry,
      customIndustry: industry === OTHER_BUSINESS_CATEGORY ? customIndustry.trim() : undefined,
    };

    try {
      const { data: newOrg } = await createOrg(orgData);
      await switchOrg(newOrg);
    } catch (e: any) {
      if (e?.data?.errors) {
        setServerErrors(e.data.errors);
        return;
      }

      if (e?.data?.message) {
        setServerErrors([e.data.message]);
        return;
      }

      setServerErrors([GENERIC_SERVER_ERROR]);
    }
  };

  const handleCancel = () => {
    if (returnToDashboard) {
      returnToDashboard();
    } else {
      window.location.href = '/dashboard';
    }
  };

  return (
    <Container>
      <H3 top={20} bottom={10}>
        {gettext('Create a new org')}
      </H3>
      <Text16 themeColor={ThemeColorType.gray}>{gettext('Tell us about your new company')}</Text16>

      <Column top={30} />

      <Column top={15}>
        <TextField
          maxLength={MAX_INPUT_LENGTH}
          ref={focusFieldRef}
          label={gettext('Company name')}
          placeholder={gettext('Company name')}
          value={companyName}
          onChange={(e): void => setCompanyName(e.currentTarget.value)}
          invalid={isSubmitted && !companyName.trim()}
          error={
            (isSubmitted && !companyName.trim() && gettext('Please provide your company name')) ||
            ''
          }
          height={INPUT_HEIGHT}
        />
      </Column>

      <Column top={15}>
        <PickerField
          label={gettext('Industry')}
          placeholder={gettext('Please select')}
          options={BUSINESS_CATEGORIES_OPTIONS}
          value={industry}
          onChange={(value: string): void => setIndustry(value)}
          invalid={isSubmitted && !industry}
          height={INPUT_HEIGHT}
        />
        <div>
          <FieldError
            invalid={isSubmitted && !industry}
            error={
              (isSubmitted && !industry && gettext('Please provide your company industry')) || ''
            }
          />
        </div>
      </Column>

      {industry === OTHER_BUSINESS_CATEGORY && (
        <Column top={15}>
          <TextField
            maxLength={MAX_INPUT_LENGTH}
            label={gettext('Industry name')}
            placeholder={gettext('Industry name')}
            value={customIndustry}
            onChange={(e): void => setCustomIndustry(e.currentTarget.value)}
            invalid={isSubmitted && !customIndustry.trim()}
            error={
              (isSubmitted &&
                !customIndustry.trim() &&
                gettext('Please provide your industry name')) ||
              ''
            }
            height={INPUT_HEIGHT}
          />
        </Column>
      )}

      <Column top={15}>
        <PickerField
          label={gettext('Number of employees')}
          placeholder={gettext('Please select')}
          options={EMPLOYEES_COUNT_OPTIONS}
          value={employeesCount}
          onChange={(value: string): void => setEmployeesCount(value)}
          height={INPUT_HEIGHT}
        />
      </Column>

      {!!serverErrors.length && (
        <Column top={15}>
          {serverErrors.map((error, i) => (
            <FieldError invalid={true} error={error} key={i} />
          ))}
        </Column>
      )}

      <ButtonContainer>
        <Button buttonType={ButtonType.default} onClick={handleCancel} height={INPUT_HEIGHT}>
          {gettext('Cancel')}
        </Button>

        <ProgressButton buttonType={ButtonType.primary} onClick={submit} height={INPUT_HEIGHT}>
          {gettext('Create')}
        </ProgressButton>
      </ButtonContainer>
    </Container>
  );
};

export default CreateOrgPage;
